import * as React from "react";
import { cn } from "../../lib/utils.js";
import { Label } from "./Label.jsx";

/**
 * Convención ÚNICA de formularios e información: ICONO · ETIQUETA · CONTROL.
 *
 * Toda fila — editable o de solo lectura — se ve igual:
 *
 *   ┌────┐  Nombre                 ← etiqueta arriba, alineada al control
 *   │ 👤 │  [ input / valor      ] ← icono a la izquierda, centrado al control
 *   └────┘  texto de ayuda
 *
 * El layout es un grid de 2 columnas con filas nombradas en vez de paddings
 * calculados a mano: la etiqueta ocupa la fila 1 de la columna derecha, y el
 * icono comparte la fila 2 con el control. Así el icono queda alineado al
 * control (no a la etiqueta) sin depender de la altura del texto — que en este
 * sistema es fluida (`clamp()`), o sea que un offset fijo se rompería.
 * Cuando no hay etiqueta la fila 1 colapsa a 0 y todo sigue cuadrando.
 *
 * Uso:
 *   <FieldGroup title="Identidad">
 *     <Field icon={User} label="Nombre" htmlFor="nombre" required>
 *       <Input id="nombre" … />
 *     </Field>
 *     <InfoField icon={Phone} label="Teléfono" value={driver.phone} />
 *   </FieldGroup>
 */

/** Caja del icono — mismo alto que un control (h-10) para que calcen exacto. */
function FieldIcon({ icon: Icon, tone = "default" }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "col-start-1 row-start-2 self-start",
        "h-10 w-10 shrink-0 rounded-lg border",
        "grid place-items-center",
        tone === "danger"
          ? "bg-danger/10 border-danger/30 text-danger-soft"
          : "bg-surface-2 border-border text-text-dim",
      )}
    >
      {Icon ? <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} /> : null}
    </div>
  );
}

/**
 * Fila editable. `children` es el control (Input, Select, Textarea, Switch,
 * DatePicker, o cualquier composición).
 *
 * @param {object} props
 * @param {import('react').ElementType} [props.icon]  Icono lucide-react.
 * @param {import('react').ReactNode} [props.label]
 * @param {string} [props.htmlFor]   id del control — hace clicable la etiqueta.
 * @param {string} [props.labelId]   Alternativa a `htmlFor` para controles que
 *   NO son labelables (DatePicker, comboboxes propios: renderizan `<button>`,
 *   y `<label for>` sobre un button no asocia nada). El consumidor pasa este
 *   mismo id como `aria-labelledby` del control.
 * @param {boolean} [props.required]
 * @param {import('react').ReactNode} [props.hint]    Ayuda bajo el control.
 * @param {string} [props.error]     Mensaje de error (reemplaza al hint).
 * @param {"vertical"|"horizontal"} [props.orientation]  `horizontal` es la
 *   variante de barra de filtros / toolbar: mismo orden icono·etiqueta·control
 *   pero en una sola línea. El icono va suelto a 16px (una caja de 40px no cabe
 *   en una toolbar) y la etiqueta al lado, no encima.
 */
export function Field({
  icon,
  label,
  htmlFor,
  labelId,
  required = false,
  hint,
  error,
  orientation = "vertical",
  className,
  controlClassName,
  children,
  ...props
}) {
  // El id del texto de ayuda cuelga del id del control cuando existe, pero cae
  // a uno generado para que los campos con `labelId` (DatePicker) o sin id
  // propio también puedan describirse.
  const generatedId = React.useId();
  const helpId =
    error || hint ? `${htmlFor ?? labelId ?? generatedId}-help` : undefined;

  // Sin esto el mensaje de error se ve pero no se anuncia: el lector de
  // pantalla lee la etiqueta y se salta el `<p>` de al lado. Se inyecta acá y
  // no en cada call site porque `Field` es quien conoce el id del texto.
  //
  // Se recorre con `Children.map` y se inyecta en el PRIMER hijo válido, no con
  // `isValidElement(children)`: varios call sites pasan el control junto a
  // avisos sueltos (InspectionPanel manda `<Input>` + dos `<p>`), y ahí
  // `children` es un array — `isValidElement` da false y el campo se quedaba
  // sin describir en silencio.
  // OJO: se recorre SIEMPRE, tenga o no `helpId`. `Children.map` re-keyea los
  // hijos, así que si solo se mapeara cuando hay hint/error, el control pasaría
  // de sin-key a keyeado justo cuando el hint aparece (p. ej. el hint de monto
  // que sale al escribir el primer dígito) y React lo remontaría: el foco se
  // perdía tras la primera tecla y el resto del número no llegaba al estado.
  let described = false;
  const describedChildren = React.Children.map(children, (child) => {
    if (!helpId || described || !React.isValidElement(child)) return child;
    described = true;
    return React.cloneElement(child, {
      // Se deduplica: un call site puede traer ya el id y `aria-describedby`
      // con el token dos veces hace que el lector de pantalla anuncie la
      // ayuda por duplicado.
      "aria-describedby": [
        ...new Set(
          `${child.props["aria-describedby"] ?? ""} ${helpId}`
            .split(/\s+/)
            .filter(Boolean),
        ),
      ].join(" "),
    });
  });

  if (orientation === "horizontal") {
    const InlineIcon = icon;
    return (
      <div
        className={cn("flex min-w-0 items-center gap-2", className)}
        {...props}
      >
        {InlineIcon ? (
          <InlineIcon
            aria-hidden="true"
            className={cn(
              "h-4 w-4 shrink-0",
              error ? "text-danger-soft" : "text-text-muted",
            )}
            strokeWidth={1.75}
          />
        ) : null}

        {label ? (
          <Label
            id={labelId}
            htmlFor={labelId ? undefined : htmlFor}
            className="shrink-0 whitespace-nowrap text-xs font-medium text-text-muted"
          >
            {label}
            {required ? (
              <span className="ml-0.5 text-danger-soft" aria-hidden="true">
                *
              </span>
            ) : null}
          </Label>
        ) : null}

        <div className={cn("min-w-0", controlClassName)}>
          {describedChildren}
        </div>

        {error ? (
          <span id={helpId} className="shrink-0 text-xs text-danger-soft">
            {error}
          </span>
        ) : hint ? (
          <span id={helpId} className="shrink-0 text-xs text-text-muted">
            {hint}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-[2.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1.5",
        "min-w-0",
        className,
      )}
      {...props}
    >
      {label ? (
        <Label
          id={labelId}
          htmlFor={labelId ? undefined : htmlFor}
          className="col-start-2 row-start-1 text-xs text-text-dim"
        >
          {label}
          {required ? (
            <span className="ml-0.5 text-danger-soft" aria-hidden="true">
              *
            </span>
          ) : null}
        </Label>
      ) : null}

      <FieldIcon icon={icon} tone={error ? "danger" : "default"} />

      <div className={cn("col-start-2 row-start-2 min-w-0", controlClassName)}>
        {describedChildren}
      </div>

      {error || hint ? (
        <p
          id={helpId}
          className={cn(
            "col-start-2 row-start-3 text-xs",
            error ? "text-danger-soft" : "text-text-muted",
          )}
        >
          {error || hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Fila de solo lectura — misma retícula que `Field`, para que una ficha de
 * detalle y su formulario de edición no se vean como dos pantallas distintas.
 * `value` vacío cae a `emptyText` en tono apagado en vez de dejar el hueco.
 *
 * `action` es la tercera columna: lo que se HACE con este dato —llamar al
 * teléfono, abrir la dirección en el mapa, el monto de un pago—. Va pegada al
 * borde derecho y no compite con el valor por el ancho: la columna es `auto`,
 * así que cuando no hay acción colapsa a 0 y la fila queda idéntica a la de un
 * formulario. Sin esta ranura cada ficha se inventaba su propio `<span
 * className="flex justify-between">` dentro del valor, y el resultado era que
 * los botones quedaban a distinta altura de fila en fila.
 *
 * @param {object} props
 * @param {import('react').ElementType} [props.icon]
 * @param {import('react').ReactNode} [props.label]
 * @param {import('react').ReactNode} [props.value]
 * @param {import('react').ReactNode} [props.hint]   Segunda línea, atenuada.
 * @param {import('react').ReactNode} [props.action] Acción(es) a la derecha —
 *   típicamente uno o dos `<InfoAction>`.
 * @param {string} [props.emptyText] — qué decir cuando NO hay valor. Sin esto
 *   la fila no se dibuja: un "Color —" no informa de nada, solo alarga la
 *   ficha y obliga a leer una fila para descubrir que no dice nada. Se pasa
 *   solo cuando el vacío ES la información ("Sin notas.", "Este cliente
 *   todavía no tiene operadores.") — ahí callarse se leería como que el dato
 *   no se cargó.
 */
export function InfoField({
  icon,
  label,
  value,
  hint,
  action,
  emptyText,
  className,
  children,
  ...props
}) {
  const content = children ?? value;
  const isEmpty =
    content === null ||
    content === undefined ||
    content === "" ||
    (typeof content === "number" && Number.isNaN(content));

  // Vacío y sin mensaje que dar: la fila desaparece. `FieldGroup` esconde la
  // tarjeta cuando se le van TODAS las filas, así que no quedan cajas huecas.
  if (isEmpty && !emptyText) return null;

  return (
    <div
      className={cn(
        "grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5",
        "min-w-0",
        className,
      )}
      {...props}
    >
      {label ? (
        <span className="col-start-2 row-start-1 text-xs font-medium leading-none text-text-dim">
          {label}
        </span>
      ) : null}

      <FieldIcon icon={icon} />

      <div
        className={cn(
          "col-start-2 row-start-2 min-w-0",
          "flex min-h-10 items-center text-sm",
          isEmpty ? "text-text-muted" : "text-text-primary",
        )}
      >
        {isEmpty ? emptyText : content}
      </div>

      {action ? (
        <div className="col-start-3 row-start-2 flex min-h-10 shrink-0 items-center gap-1.5">
          {action}
        </div>
      ) : null}

      {hint ? (
        <p className="col-start-2 row-start-3 text-xs text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

const INFO_ACTION_TONES = {
  default:
    "border-border bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text-primary",
  primary: "border-primary/30 bg-primary/10 text-primary hover:bg-primary/20",
  success:
    "border-success/30 bg-success/10 text-success-soft hover:bg-success/20",
  danger: "border-danger/30 bg-danger/10 text-danger-soft hover:bg-danger/20",
};

/**
 * Acción de una fila de información — el botón/enlace de la tercera columna.
 *
 * Solo-icono por defecto (llamar, WhatsApp): ahí `label` es el nombre
 * accesible, porque un botón con puro `<svg aria-hidden>` no tiene ninguno y el
 * lector de pantalla lo anuncia como "botón" a secas. Con `children` pasa a ser
 * un botón con texto ("Mapa", "Fijar aquí") y `label` queda solo de tooltip.
 *
 * @param {object} props
 * @param {import('react').ElementType} [props.icon]
 * @param {string} props.label — nombre accesible / tooltip. Obligatorio.
 * @param {"default"|"primary"|"success"|"danger"} [props.tone]
 * @param {string} [props.href] — lo vuelve enlace (`tel:`, `https://…`).
 * @param {boolean} [props.external] — abre en pestaña nueva (solo con `href`).
 */
export function InfoAction({
  icon: Icon,
  label,
  tone = "default",
  href,
  external = false,
  className,
  children,
  ...props
}) {
  const Cmp = href ? "a" : "button";
  const iconOnly = children === undefined || children === null;

  return (
    <Cmp
      {...(href
        ? {
            href,
            ...(external
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {}),
          }
        : { type: "button" })}
      aria-label={iconOnly ? label : undefined}
      title={label}
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border",
        "text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
        iconOnly ? "h-9 w-9" : "h-9 px-2.5",
        INFO_ACTION_TONES[tone] ?? INFO_ACTION_TONES.default,
        className,
      )}
      {...props}
    >
      {Icon ? (
        <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
      ) : null}
      {children}
    </Cmp>
  );
}

/**
 * Tarjeta que agrupa filas, con separadores entre ellas (la "Identidad" /
 * "Dirección" del diseño). `title` se renderiza FUERA de la tarjeta, como
 * encabezado de sección.
 */
export function FieldGroup({ title, description, className, children, ...props }) {
  return (
    // `:has(> div:empty)` esconde la sección ENTERA —título incluido— cuando
    // todas sus filas se fueron por falta de datos. Va en CSS y no contando
    // hijos en JS a propósito: `children` son elementos `<InfoField>`, y desde
    // acá no hay forma de saber cuáles de ellos van a devolver `null` sin
    // renderizarlos. El DOM sí lo sabe.
    <section
      className={cn(
        "min-w-0 space-y-2 [&:has(>div:empty)]:hidden",
        className,
      )}
      {...props}
    >
      {title ? (
        <div className="space-y-0.5">
          <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
          {description ? (
            <p className="text-xs text-text-muted">{description}</p>
          ) : null}
        </div>
      ) : null}
      {/*
        El relleno de cada fila se aplica por CSS a los hijos directos, NO
        envolviendo cada hijo en un div keyeado por índice: `toArray` colapsa
        los hijos falsos de un `{cond && <Field/>}`, así que al alternar esa
        condición todas las filas siguientes cambiaban de key y React las
        remontaba (foco perdido a media escritura, DatePicker abierto que se
        cierra solo). Dejando pasar los children tal cual, JSX conserva el
        hueco y las hermanas mantienen su identidad.
      */}
      <div className="rounded-xl border border-border bg-surface-1 sheen-1 shadow-card divide-y divide-border-muted [&>*]:px-4 [&>*]:py-3">
        {children}
      </div>
    </section>
  );
}

/**
 * Fila de AJUSTE: `[control] icono Acción` arriba y la descripción debajo.
 *
 *   ┌────┐
 *   │ ◉  │  ⚙ Registrar en bloque      ← el control manda: se lee "prendido/
 *   └────┘                               apagado" antes que el nombre
 *           Se cargan todos y se registran juntos   ← qué hace, alineado bajo
 *                                                     la acción
 *
 * Es la variante de `Field` para un interruptor dentro de un panel angosto:
 * `orientation="horizontal"` deja la ayuda EN LA MISMA línea (icono·etiqueta·
 * control·ayuda) y con un texto de seis palabras la fila se desborda. Acá la
 * ayuda baja a su propio renglón y la fila deja de pelear por el ancho.
 *
 * El cableado de accesibilidad va acá y no en cada call site: al control se le
 * inyectan `aria-labelledby` (la acción) y `aria-describedby` (la descripción),
 * que es lo que un `<label>` no puede hacer sobre un `<button role="switch">`.
 *
 * @param {import('react').ElementType} [props.icon] Icono lucide-react.
 * @param {string} props.label        La acción, en imperativo.
 * @param {string} [props.description] Qué hace. Se anuncia con el control.
 * @param {string} [props.labelId]    Id propio si el call site lo necesita.
 */
export function ToggleField({
  icon: Icon,
  label,
  description,
  labelId,
  className,
  children,
  ...props
}) {
  const generatedId = React.useId();
  const id = labelId ?? `${generatedId}-label`;
  const helpId = description ? `${generatedId}-help` : undefined;

  // Mismo patrón que `Field`: se recorre SIEMPRE (tenga o no descripción) para
  // no re-keyear el control cuando la descripción aparece o desaparece.
  let wired = false;
  const wiredChildren = React.Children.map(children, (child) => {
    if (wired || !React.isValidElement(child)) return child;
    wired = true;
    return React.cloneElement(child, {
      "aria-labelledby": child.props["aria-labelledby"] ?? id,
      ...(helpId
        ? {
            "aria-describedby": [
              ...new Set(
                `${child.props["aria-describedby"] ?? ""} ${helpId}`
                  .split(/\s+/)
                  .filter(Boolean),
              ),
            ].join(" "),
          }
        : {}),
    });
  });

  return (
    // El grid es lo que alinea la descripción bajo la acción sin un padding
    // calculado a mano: la primera columna la fija el ancho del control.
    <div
      className={cn("grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2.5", className)}
      {...props}
    >
      {wiredChildren}
      <div className="flex min-w-0 items-center gap-1.5">
        {Icon ? (
          <Icon
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-text-muted"
            strokeWidth={1.75}
          />
        ) : null}
        <Label id={id} className="text-xs font-medium text-text-primary">
          {label}
        </Label>
      </div>
      {description ? (
        <p id={helpId} className="col-start-2 mt-0.5 text-xs text-text-muted">
          {description}
        </p>
      ) : null}
    </div>
  );
}

Field.Group = FieldGroup;
Field.Action = InfoAction;
Field.Info = InfoField;
Field.Toggle = ToggleField;
